import * as THREE from 'three';
import { Spell } from './Spell';
import { SpellType } from '../game/CooldownManager';

export interface CollisionResult {
  spell1: Spell;
  spell2: Spell;
  collisionPoint: THREE.Vector3;
  type: CollisionType;
}

export type CollisionType = 'fireball_fireball' | 'fireball_iceShard' | 'generic';

/**
 * Detects and handles mid-air spell collisions between opposing projectiles.
 * Lightning passes through everything (too fast to collide).
 */
export class SpellCollisionSystem {
  private scene: THREE.Scene;
  private static readonly COLLISION_DISTANCE = 1.5;

  constructor(scene: THREE.Scene) {
    this.scene = scene;
  }

  /**
   * Check for collisions among active spells.
   * Returns pairs of spells that collided this frame.
   * Only checks opposing spells (player vs enemy projectiles).
   */
  checkCollisions(activeSpells: Set<Spell>): CollisionResult[] {
    const results: CollisionResult[] = [];
    const spellArray = Array.from(activeSpells);

    // Separate player and enemy projectile spells
    const playerProjectiles: Spell[] = [];
    const enemyProjectiles: Spell[] = [];

    for (const spell of spellArray) {
      const type = spell.getType();
      // Lightning is too fast to collide - skip it
      if (type === 'lightningBolt') continue;
      // Only projectile spells can collide mid-air
      if (type === 'fireball' || type === 'iceShard') {
        if (spell.isFromPlayer()) {
          playerProjectiles.push(spell);
        } else {
          enemyProjectiles.push(spell);
        }
      }
    }

    // Check each player projectile against each enemy projectile
    for (const pSpell of playerProjectiles) {
      for (const eSpell of enemyProjectiles) {
        const dist = pSpell.getPosition().distanceTo(eSpell.getPosition());
        if (dist < SpellCollisionSystem.COLLISION_DISTANCE) {
          const midpoint = pSpell.getPosition().add(eSpell.getPosition()).multiplyScalar(0.5);
          const collisionType = this.getCollisionType(pSpell.getType(), eSpell.getType());
          results.push({
            spell1: pSpell,
            spell2: eSpell,
            collisionPoint: midpoint,
            type: collisionType,
          });
        }
      }
    }

    return results;
  }

  private getCollisionType(type1: SpellType, type2: SpellType): CollisionType {
    const types = [type1, type2].sort();
    if (types[0] === 'fireball' && types[1] === 'fireball') return 'fireball_fireball';
    if (types[0] === 'fireball' && types[1] === 'iceShard') return 'fireball_iceShard';
    return 'generic';
  }

  /**
   * Create the visual effect for a spell collision.
   */
  createCollisionEffect(result: CollisionResult): void {
    switch (result.type) {
      case 'fireball_fireball':
        this.createDoubleExplosion(result.collisionPoint);
        break;
      case 'fireball_iceShard':
        this.createSteamCloud(result.collisionPoint);
        break;
      default:
        this.createGenericCollision(result.collisionPoint);
        break;
    }
  }

  /**
   * Fireball vs Fireball: Double explosion at midpoint.
   * Spectacular, neither deals damage.
   */
  private createDoubleExplosion(position: THREE.Vector3): void {
    const particleCount = 200;
    const positions = new Float32Array(particleCount * 3);
    const colors = new Float32Array(particleCount * 3);
    const velocities: THREE.Vector3[] = [];

    for (let i = 0; i < particleCount; i++) {
      positions[i * 3] = position.x;
      positions[i * 3 + 1] = position.y;
      positions[i * 3 + 2] = position.z;

      // Hot white-yellow core -> orange -> deep red outer
      const t = i / particleCount;
      if (t < 0.15) {
        colors[i * 3] = 1; colors[i * 3 + 1] = 1; colors[i * 3 + 2] = 0.8;
      } else if (t < 0.4) {
        colors[i * 3] = 1; colors[i * 3 + 1] = 0.6 + Math.random() * 0.3; colors[i * 3 + 2] = 0;
      } else if (t < 0.7) {
        colors[i * 3] = 1; colors[i * 3 + 1] = 0.2 + Math.random() * 0.2; colors[i * 3 + 2] = 0;
      } else {
        colors[i * 3] = 0.6; colors[i * 3 + 1] = 0.05; colors[i * 3 + 2] = 0;
      }

      const theta = Math.random() * Math.PI * 2;
      const phi = Math.random() * Math.PI;
      const speed = 5 + Math.random() * 10;
      velocities.push(new THREE.Vector3(
        Math.sin(phi) * Math.cos(theta) * speed,
        Math.sin(phi) * Math.sin(theta) * speed * 0.8 + 3,
        Math.cos(phi) * speed,
      ));
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));

    const material = new THREE.PointsMaterial({
      size: 0.3,
      vertexColors: true,
      transparent: true,
      opacity: 1,
      blending: THREE.AdditiveBlending,
    });

    const explosion = new THREE.Points(geometry, material);
    this.scene.add(explosion);

    // Double flash lights
    const light1 = new THREE.PointLight(0xFF4400, 6, 20);
    light1.position.copy(position);
    this.scene.add(light1);

    const light2 = new THREE.PointLight(0xFFAA00, 4, 15);
    light2.position.copy(position);
    light2.position.y += 1;
    this.scene.add(light2);

    // Expanding shockwave rings
    const ring1Geo = new THREE.RingGeometry(0.3, 1.0, 32);
    const ring1Mat = new THREE.MeshBasicMaterial({
      color: 0xFF6600,
      transparent: true,
      opacity: 0.8,
      side: THREE.DoubleSide,
    });
    const ring1 = new THREE.Mesh(ring1Geo, ring1Mat);
    ring1.position.copy(position);
    ring1.rotation.x = -Math.PI / 2;
    this.scene.add(ring1);

    const ring2Geo = new THREE.RingGeometry(0.2, 0.8, 32);
    const ring2Mat = new THREE.MeshBasicMaterial({
      color: 0xFFAA00,
      transparent: true,
      opacity: 0.6,
      side: THREE.DoubleSide,
    });
    const ring2 = new THREE.Mesh(ring2Geo, ring2Mat);
    ring2.position.copy(position);
    ring2.rotation.z = Math.PI / 2;
    this.scene.add(ring2);

    let elapsed = 0;
    const animate = () => {
      elapsed += 0.016;

      const pos = explosion.geometry.attributes.position.array as Float32Array;
      for (let i = 0; i < particleCount; i++) {
        pos[i * 3] += velocities[i].x * 0.016;
        pos[i * 3 + 1] += velocities[i].y * 0.016;
        pos[i * 3 + 2] += velocities[i].z * 0.016;
        velocities[i].y -= 6 * 0.016;
      }
      explosion.geometry.attributes.position.needsUpdate = true;

      material.opacity = Math.max(0, 1 - elapsed / 0.9);
      light1.intensity = 6 * Math.max(0, 1 - elapsed / 0.4);
      light2.intensity = 4 * Math.max(0, 1 - elapsed / 0.5);

      ring1.scale.setScalar(1 + elapsed * 8);
      ring1Mat.opacity = 0.8 * Math.max(0, 1 - elapsed / 0.5);
      ring2.scale.setScalar(1 + elapsed * 6);
      ring2Mat.opacity = 0.6 * Math.max(0, 1 - elapsed / 0.4);

      if (elapsed < 0.9) {
        requestAnimationFrame(animate);
      } else {
        this.scene.remove(explosion);
        this.scene.remove(light1);
        this.scene.remove(light2);
        this.scene.remove(ring1);
        this.scene.remove(ring2);
        geometry.dispose();
        material.dispose();
        light1.dispose();
        light2.dispose();
        ring1Geo.dispose();
        ring1Mat.dispose();
        ring2Geo.dispose();
        ring2Mat.dispose();
      }
    };
    requestAnimationFrame(animate);
  }

  /**
   * Fireball vs Ice Shard: Steam cloud at collision point.
   * Deals 50% of each spell's damage as AOE to both players.
   */
  private createSteamCloud(position: THREE.Vector3): void {
    const particleCount = 120;
    const positions = new Float32Array(particleCount * 3);
    const colors = new Float32Array(particleCount * 3);
    const velocities: THREE.Vector3[] = [];

    for (let i = 0; i < particleCount; i++) {
      positions[i * 3] = position.x + (Math.random() - 0.5) * 0.5;
      positions[i * 3 + 1] = position.y + (Math.random() - 0.5) * 0.5;
      positions[i * 3 + 2] = position.z + (Math.random() - 0.5) * 0.5;

      // Steam: white-grey with hints of blue and orange
      const t = Math.random();
      if (t < 0.4) {
        // White steam
        const shade = 0.7 + Math.random() * 0.3;
        colors[i * 3] = shade; colors[i * 3 + 1] = shade; colors[i * 3 + 2] = shade;
      } else if (t < 0.6) {
        // Blue tint from ice
        colors[i * 3] = 0.6; colors[i * 3 + 1] = 0.7; colors[i * 3 + 2] = 0.9;
      } else if (t < 0.8) {
        // Orange tint from fire
        colors[i * 3] = 0.9; colors[i * 3 + 1] = 0.6; colors[i * 3 + 2] = 0.3;
      } else {
        // Grey smoke
        const shade = 0.4 + Math.random() * 0.2;
        colors[i * 3] = shade; colors[i * 3 + 1] = shade; colors[i * 3 + 2] = shade;
      }

      // Steam rises and expands slowly
      const theta = Math.random() * Math.PI * 2;
      const speed = 1 + Math.random() * 3;
      velocities.push(new THREE.Vector3(
        Math.cos(theta) * speed,
        1 + Math.random() * 3,
        Math.sin(theta) * speed,
      ));
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));

    const material = new THREE.PointsMaterial({
      size: 0.35,
      vertexColors: true,
      transparent: true,
      opacity: 0.8,
      blending: THREE.AdditiveBlending,
    });

    const steam = new THREE.Points(geometry, material);
    this.scene.add(steam);

    // Flash at collision
    const light = new THREE.PointLight(0xFFDDCC, 3, 12);
    light.position.copy(position);
    this.scene.add(light);

    let elapsed = 0;
    const animate = () => {
      elapsed += 0.016;

      const pos = steam.geometry.attributes.position.array as Float32Array;
      for (let i = 0; i < particleCount; i++) {
        pos[i * 3] += velocities[i].x * 0.016;
        pos[i * 3 + 1] += velocities[i].y * 0.016;
        pos[i * 3 + 2] += velocities[i].z * 0.016;
        // Steam decelerates
        velocities[i].multiplyScalar(0.98);
      }
      steam.geometry.attributes.position.needsUpdate = true;

      material.opacity = 0.8 * Math.max(0, 1 - elapsed / 1.2);
      material.size = 0.35 + elapsed * 0.3; // Steam particles grow
      light.intensity = 3 * Math.max(0, 1 - elapsed / 0.3);

      if (elapsed < 1.2) {
        requestAnimationFrame(animate);
      } else {
        this.scene.remove(steam);
        this.scene.remove(light);
        geometry.dispose();
        material.dispose();
        light.dispose();
      }
    };
    requestAnimationFrame(animate);
  }

  /**
   * Generic collision for spell types without special interaction.
   */
  private createGenericCollision(position: THREE.Vector3): void {
    const particleCount = 60;
    const positions = new Float32Array(particleCount * 3);
    const velocities: THREE.Vector3[] = [];

    for (let i = 0; i < particleCount; i++) {
      positions[i * 3] = position.x;
      positions[i * 3 + 1] = position.y;
      positions[i * 3 + 2] = position.z;

      const theta = Math.random() * Math.PI * 2;
      const phi = Math.random() * Math.PI;
      const speed = 3 + Math.random() * 5;
      velocities.push(new THREE.Vector3(
        Math.sin(phi) * Math.cos(theta) * speed,
        Math.sin(phi) * Math.sin(theta) * speed,
        Math.cos(phi) * speed,
      ));
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));

    const material = new THREE.PointsMaterial({
      color: 0xFFFFFF,
      size: 0.2,
      transparent: true,
      opacity: 1,
      blending: THREE.AdditiveBlending,
    });

    const explosion = new THREE.Points(geometry, material);
    this.scene.add(explosion);

    let elapsed = 0;
    const animate = () => {
      elapsed += 0.016;

      const pos = explosion.geometry.attributes.position.array as Float32Array;
      for (let i = 0; i < particleCount; i++) {
        pos[i * 3] += velocities[i].x * 0.016;
        pos[i * 3 + 1] += velocities[i].y * 0.016;
        pos[i * 3 + 2] += velocities[i].z * 0.016;
      }
      explosion.geometry.attributes.position.needsUpdate = true;
      material.opacity = Math.max(0, 1 - elapsed / 0.5);

      if (elapsed < 0.5) {
        requestAnimationFrame(animate);
      } else {
        this.scene.remove(explosion);
        geometry.dispose();
        material.dispose();
      }
    };
    requestAnimationFrame(animate);
  }

  /**
   * Get the AOE damage for a steam cloud collision.
   * Returns 50% of each spell's damage dealt to both players.
   */
  getCollisionDamage(result: CollisionResult): { playerDamage: number; enemyDamage: number } {
    if (result.type === 'fireball_iceShard') {
      const damage1 = result.spell1.getDamage() * 0.5;
      const damage2 = result.spell2.getDamage() * 0.5;
      const totalAoe = Math.round(damage1 + damage2);
      return { playerDamage: totalAoe, enemyDamage: totalAoe };
    }
    // Fireball vs Fireball: no damage to either
    return { playerDamage: 0, enemyDamage: 0 };
  }
}
